import React, { useState, useEffect } from 'react';
import { useForm } from '@tanstack/react-form';
import {
  ArrowRight,
  TrendingUp,
  Users,
  Trophy,
  Zap,
  DollarSign,
  HeadphonesIcon,
  CheckCircle,
  ArrowUpRight,
  Sparkles,
} from 'lucide-react';
import { enquireSchema } from '@/features/application/schemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { reachGoal } from '@/features/analytics/yandex-metrika';
import { apiClient } from '@/lib/api';
import SEO from '@/features/seo/SEO';

// Testimonial data
const testimonials = [
  {
    id: 1,
    text: 'Сервис позволил нам увеличить средний чек и конверсию. Очень довольны результатами сотрудничества.',
    company: 'Товары для птицеводства',
    increase: '+45% к продажам',
  },
  {
    id: 2,
    text: 'Мы заметили значительный рост продаж после интеграции сервиса. Клиенты довольны возможностью покупки в кредит.',
    company: 'Магазин инструментов',
    increase: '+60% конверсия',
  },
  {
    id: 3,
    text: 'Простая интеграция и отличная поддержка. Наши B2B клиенты оценили возможность отсрочки платежа.',
    company: 'Оптовая компания',
    increase: '+35% новых клиентов',
  },
  {
    id: 4,
    text: 'Увеличение среднего чека на 40% за первый месяц работы. Рекомендуем всем B2B компаниям.',
    company: 'Строительные материалы',
    increase: '+40% средний чек',
  },
];

// Partner logos placeholder - commented out until we have real partners
// const partnerLogos = [
//   { id: 1, name: 'Partner 1', logo: '🏢' },
//   { id: 2, name: 'Partner 2', logo: '🏗️' },
//   { id: 3, name: 'Partner 3', logo: '🏭' },
//   { id: 4, name: 'Partner 4', logo: '🏬' },
//   { id: 5, name: 'Partner 5', logo: '🏪' },
//   { id: 6, name: 'Partner 6', logo: '🏛️' },
// ];

const LandingPage: React.FC = () => {
  const [submitMessage, setSubmitMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  const form = useForm({
    defaultValues: { name: '', phone: '' },
    validators: { onChange: enquireSchema },
    onSubmit: async ({ value }) => {
      setSubmitMessage(null);
      try {
        const { data: response } = await apiClient.POST('/api/shop/enquire', {
          body: { name: value.name, phone: value.phone, source: 'landing_page' },
        });
        if (response?.success) {
          reachGoal('form_submit_success', {
            source: 'landing_page',
            name: value.name,
            timestamp: new Date().toISOString(),
          });
          setSubmitMessage({
            type: 'success',
            text: 'Заявка успешно отправлена! Мы свяжемся с вами в ближайшее время.',
          });
          form.reset();
        } else {
          setSubmitMessage({
            type: 'error',
            text: response?.message || 'Не удалось отправить заявку. Попробуйте позже.',
          });
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        setSubmitMessage({
          type: 'error',
          text: 'Произошла ошибка при отправке заявки. Попробуйте позже.',
        });
      }
    },
  });

  // States for animated numbers
  const [shopsCount, setShopsCount] = useState(0);
  const [growthPercent, setGrowthPercent] = useState(0);
  const [supportHours, setSupportHours] = useState(0);

  const handleTelegramClick = () => {
    // Track Yandex Metrika goal
    reachGoal('telegram_click', {
      source: 'landing_page',
      timestamp: new Date().toISOString(),
    });
    // Open telegram link
    window.open('https://t.me/lona_manager', '_blank');
  };

  // Animate numbers on mount. Tracks all intervals and clears them on
  // unmount so React doesn't try to setState on a torn-down tree
  // (jsdom tests + StrictMode both surface this).
  useEffect(() => {
    const timers: ReturnType<typeof setInterval>[] = [];
    const animateValue = (
      setter: React.Dispatch<React.SetStateAction<number>>,
      target: number,
      duration: number
    ) => {
      const steps = 60;
      const increment = target / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          setter(target);
          clearInterval(timer);
        } else {
          setter(Math.floor(current));
        }
      }, duration / steps);
      timers.push(timer);
    };

    animateValue(setShopsCount, 100, 2000);
    animateValue(setGrowthPercent, 45, 1800);
    animateValue(setSupportHours, 24, 1500);

    return () => {
      for (const t of timers) clearInterval(t);
    };
  }, []);

  return (
    <>
      <SEO
        title="ЛОНА ФИНАНС - Увеличивайте B2B продажи с кредитованием покупателей | Рост продаж на 40-60%"
        description="ЛОНА ФИНАНС - Увеличивайте B2B продажи на 40-60% с кредитованием покупателей. Интеграция за 1 день, 0₽ для магазина. Персональный менеджер и поддержка 24/7."
        keywords="B2B кредитование, увеличение продаж, финансирование покупателей, кредит для бизнеса, торговое финансирование, отсрочка платежа, факторинг, ЛОНА ФИНАНС"
        type="website"
        url="https://lona.finance/"
        ogTitle="ЛОНА ФИНАНС - Увеличивайте B2B продажи с кредитованием покупателей"
        ogDescription="Развивайте свой B2B бизнес с помощью нашей платформы. Позвольте клиентам покупать в кредит и увеличьте продажи на 40-60%."
      />
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        {/* Header */}
        <header className="sticky top-0 z-50 border-b border-gray-100 bg-white/80 backdrop-blur-md">
          <div className="container mx-auto px-4 py-3 md:py-4">
            <div className="flex min-h-12 items-center justify-between md:min-h-0">
              <div className="flex items-center space-x-3">
                <img
                  src="/small-logo.svg"
                  alt="ЛОНА ФИНАНС"
                  className="size-8"
                  onError={e => {
                    e.currentTarget.style.display = 'none';
                    const next = e.currentTarget.nextElementSibling;
                    if (next) {
                      next.classList.remove('hidden');
                      next.classList.add('flex');
                    }
                  }}
                />
                <div className="hidden size-8 items-center justify-center rounded-lg bg-black">
                  <span className="text-lg font-bold text-white">L</span>
                </div>
                <div>
                  <span className="text-xl font-bold text-black">ЛОНА</span>
                  <span className="ml-1 text-xl font-light text-gray-600">ФИНАНС</span>
                </div>
              </div>
              <Button
                variant="outline"
                className="hidden items-center gap-2 transition-all hover:bg-black hover:text-white md:flex"
                onClick={handleTelegramClick}
              >
                Начать сотрудничество
                <ArrowUpRight className="size-4" />
              </Button>
            </div>
          </div>
        </header>

        {/* Hero Section with Animation */}
        <section className="relative overflow-hidden px-4 py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50 opacity-50" />
          <div className="container relative mx-auto max-w-6xl">
            <div className="animate-fade-in space-y-6 text-center">
              <Badge className="mb-4 border-black/10 bg-black/5 px-4 py-2 text-black hover:bg-black/5">
                <Sparkles className="mr-2 size-3" />
                Увеличьте продажи на 40-60%
              </Badge>
              <h1 className="text-4xl font-bold leading-tight text-black md:text-6xl lg:text-7xl">
                Увеличивайте B2B продажи
                <span className="mt-2 block text-gray-600">с рассрочкой для покупателей</span>
              </h1>
              <p className="mx-auto max-w-3xl text-lg leading-relaxed text-gray-600 md:text-xl">
                Развивайте свой B2B бизнес с помощью нашей платформы. Позвольте клиентам покупать в
                рассрочку или кредит и увеличьте продажи, лояльность и конкурентное преимущество.
              </p>
              <div className="flex flex-col justify-center gap-4 pt-8 sm:flex-row">
                <Button
                  size="lg"
                  className="rounded-xl bg-black px-8 py-6 text-lg text-white shadow-xl transition-all hover:scale-105 hover:bg-gray-900 hover:shadow-2xl"
                  onClick={() => {
                    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Оставить заявку
                  <ArrowRight className="ml-2 size-5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-xl border-2 px-8 py-6 text-lg"
                  onClick={handleTelegramClick}
                >
                  Узнать подробнее
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="border-y border-gray-100 px-4 py-12">
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-2 gap-8 text-center md:grid-cols-4">
              <div>
                <div className="text-3xl font-bold text-black transition-all duration-300 md:text-4xl">
                  {shopsCount}+
                </div>
                <div className="mt-1 text-sm text-gray-600">Активных магазинов</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-black transition-all duration-300 md:text-4xl">
                  {growthPercent}%
                </div>
                <div className="mt-1 text-sm text-gray-600">Рост продаж</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-black md:text-4xl">0₽</div>
                <div className="mt-1 text-sm text-gray-600">Для магазина</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-black transition-all duration-300 md:text-4xl">
                  {supportHours}/7
                </div>
                <div className="mt-1 text-sm text-gray-600">Поддержка</div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section with Hover Effects */}
        <section className="px-4 py-12 md:py-20">
          <div className="container mx-auto max-w-6xl">
            <div className="mb-8 text-center md:mb-12">
              <h2 className="mb-4 text-3xl font-bold text-black md:text-5xl">
                Почему выбирают ЛОНА
              </h2>
              <p className="mx-auto max-w-2xl text-base text-gray-600 md:text-lg">
                Предложите клиентам гибкие условия оплаты для увеличения заказов и долгосрочных
                отношений
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
              <Card className="group border-gray-200 transition-all duration-300 hover:-translate-y-1 hover:border-black hover:shadow-2xl">
                <CardContent className="p-5 md:p-8">
                  <div className="flex items-start space-x-3 md:space-x-4">
                    <div className="rounded-lg bg-black p-2 text-white transition-transform group-hover:scale-110 md:rounded-xl md:p-3">
                      <TrendingUp className="size-5 md:size-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="mb-1 text-lg font-semibold text-black md:mb-2 md:text-xl">
                        Увеличение продаж
                      </h3>
                      <p className="text-sm leading-relaxed text-gray-600 md:text-base">
                        Возможность покупки в рассрочку повышает средний чек и конверсию за счёт
                        более крупных заказов
                      </p>
                      <div className="mt-3 md:mt-4">
                        <Badge
                          variant="outline"
                          className="border-green-200 bg-green-50 text-xs text-green-700 md:text-sm"
                        >
                          +45% к среднему чеку
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="group border-gray-200 transition-all duration-300 hover:-translate-y-1 hover:border-black hover:shadow-2xl">
                <CardContent className="p-5 md:p-8">
                  <div className="flex items-start space-x-3 md:space-x-4">
                    <div className="rounded-lg bg-black p-2 text-white transition-transform group-hover:scale-110 md:rounded-xl md:p-3">
                      <Users className="size-5 md:size-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="mb-1 text-lg font-semibold text-black md:mb-2 md:text-xl">
                        Лояльность клиентов
                      </h3>
                      <p className="text-sm leading-relaxed text-gray-600 md:text-base">
                        Удерживайте клиентов, предлагая удобные и гибкие условия оплаты
                      </p>
                      <div className="mt-3 md:mt-4">
                        <Badge
                          variant="outline"
                          className="border-blue-200 bg-blue-50 text-xs text-blue-700 md:text-sm"
                        >
                          90% возвращаются
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="group border-gray-200 transition-all duration-300 hover:-translate-y-1 hover:border-black hover:shadow-2xl">
                <CardContent className="p-5 md:p-8">
                  <div className="flex items-start space-x-3 md:space-x-4">
                    <div className="rounded-lg bg-black p-2 text-white transition-transform group-hover:scale-110 md:rounded-xl md:p-3">
                      <Trophy className="size-5 md:size-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="mb-1 text-lg font-semibold text-black md:mb-2 md:text-xl">
                        Конкурентное преимущество
                      </h3>
                      <p className="text-sm leading-relaxed text-gray-600 md:text-base">
                        Выделитесь на рынке, предлагая уникальные условия
                      </p>
                      <div className="mt-3 md:mt-4">
                        <Badge
                          variant="outline"
                          className="border-purple-200 bg-purple-50 text-xs text-purple-700 md:text-sm"
                        >
                          Уникальное предложение
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="group border-gray-200 transition-all duration-300 hover:-translate-y-1 hover:border-black hover:shadow-2xl">
                <CardContent className="p-5 md:p-8">
                  <div className="flex items-start space-x-3 md:space-x-4">
                    <div className="rounded-lg bg-black p-2 text-white transition-transform group-hover:scale-110 md:rounded-xl md:p-3">
                      <Zap className="size-5 md:size-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="mb-1 text-lg font-semibold text-black md:mb-2 md:text-xl">
                        Простота подключения
                      </h3>
                      <p className="text-sm leading-relaxed text-gray-600 md:text-base">
                        Легкая интеграция и поддержка без начальных затрат для магазина
                      </p>
                      <div className="mt-3 md:mt-4">
                        <Badge
                          variant="outline"
                          className="border-orange-200 bg-orange-50 text-xs text-orange-700 md:text-sm"
                        >
                          Запуск за 1 день
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Key Features Section */}
        <section className="bg-gray-50 px-4 py-12 md:py-20">
          <div className="container mx-auto max-w-6xl">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-3xl font-bold text-black md:text-5xl">
                Всё что нужно для роста бизнеса
              </h2>
              <p className="mx-auto max-w-3xl text-lg text-gray-600">
                Платформа бесплатна для магазинов. Комиссию платит покупатель. Личный кабинет для
                полного контроля.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              <div className="group text-center">
                <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-2xl bg-white p-4 shadow-lg transition-all group-hover:scale-110 group-hover:shadow-xl md:mb-6 md:size-24 md:p-6">
                  <Zap className="size-8 text-black md:size-10" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-black md:mb-3 md:text-xl">
                  Быстрый старт
                </h3>
                <p className="text-sm leading-relaxed text-gray-600 md:text-base">
                  Интеграция за 1 день с персональной поддержкой на всех этапах
                </p>
              </div>

              <div className="group text-center">
                <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-2xl bg-white p-4 shadow-lg transition-all group-hover:scale-110 group-hover:shadow-xl md:mb-6 md:size-24 md:p-6">
                  <DollarSign className="size-8 text-black md:size-10" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-black md:mb-3 md:text-xl">
                  0₽ для магазина
                </h3>
                <p className="text-sm leading-relaxed text-gray-600 md:text-base">
                  Комиссию платит покупатель, магазин получает деньги сразу
                </p>
              </div>

              <div className="group text-center">
                <div className="mx-auto mb-4 flex size-16 items-center justify-center rounded-2xl bg-white p-4 shadow-lg transition-all group-hover:scale-110 group-hover:shadow-xl md:mb-6 md:size-24 md:p-6">
                  <HeadphonesIcon className="size-8 text-black md:size-10" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-black md:mb-3 md:text-xl">
                  Личный кабинет
                </h3>
                <p className="text-sm leading-relaxed text-gray-600 md:text-base">
                  Полная статистика и контроль всех заявок в реальном времени
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="px-4 py-12 md:py-20">
          <div className="container mx-auto max-w-6xl">
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-3xl font-bold text-black md:text-5xl">
                Начните за 3 простых шага
              </h2>
              <p className="mx-auto max-w-3xl text-lg text-gray-600">
                Подключитесь к платформе и начните увеличивать продажи уже завтра
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
              <div className="group relative">
                <div className="absolute left-full top-8 hidden h-0.5 w-full bg-gray-200 md:block" />
                <div className="rounded-2xl bg-white p-5 shadow-lg transition-all hover:-translate-y-2 hover:shadow-2xl md:p-8">
                  <div className="mb-3 text-4xl font-bold text-gray-100 md:mb-4 md:text-6xl">
                    01
                  </div>
                  <div className="mb-2 flex items-center gap-2 md:mb-3 md:block">
                    <h3 className="text-lg font-semibold text-black md:text-xl">Оставьте заявку</h3>
                    <CheckCircle className="size-5 shrink-0 text-green-500 md:hidden" />
                  </div>
                  <p className="text-sm text-gray-600 md:text-base">
                    Заполните форму и получите персональную консультацию
                  </p>
                  <CheckCircle className="mt-4 hidden size-6 text-green-500 md:block" />
                </div>
              </div>

              <div className="group relative">
                <div className="absolute left-full top-8 hidden h-0.5 w-full bg-gray-200 md:block" />
                <div className="rounded-2xl bg-white p-5 shadow-lg transition-all hover:-translate-y-2 hover:shadow-2xl md:p-8">
                  <div className="mb-3 text-4xl font-bold text-gray-100 md:mb-4 md:text-6xl">
                    02
                  </div>
                  <div className="mb-2 flex items-center gap-2 md:mb-3 md:block">
                    <h3 className="text-lg font-semibold text-black md:text-xl">Интеграция</h3>
                    <CheckCircle className="size-5 shrink-0 text-green-500 md:hidden" />
                  </div>
                  <p className="text-sm text-gray-600 md:text-base">
                    Быстрая настройка платформы с помощью нашей команды
                  </p>
                  <CheckCircle className="mt-4 hidden size-6 text-green-500 md:block" />
                </div>
              </div>

              <div className="group relative">
                <div className="rounded-2xl bg-white p-5 shadow-lg transition-all hover:-translate-y-2 hover:shadow-2xl md:p-8">
                  <div className="mb-3 text-4xl font-bold text-gray-100 md:mb-4 md:text-6xl">
                    03
                  </div>
                  <div className="mb-2 flex items-center gap-2 md:mb-3 md:block">
                    <h3 className="text-lg font-semibold text-black md:text-xl">Рост продаж</h3>
                    <CheckCircle className="size-5 shrink-0 text-green-500 md:hidden" />
                  </div>
                  <p className="text-sm text-gray-600 md:text-base">
                    Начните принимать заявки и увеличивайте выручку
                  </p>
                  <CheckCircle className="mt-4 hidden size-6 text-green-500 md:block" />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="bg-gray-50 py-12 md:py-20">
          <div className="mb-8 px-4 text-center md:mb-16">
            <h2 className="mb-4 text-3xl font-bold text-black md:text-5xl">
              Отзывы наших партнёров
            </h2>
            <p className="text-lg text-gray-600">Более 100 компаний уже увеличили продажи с ЛОНА</p>
          </div>

          <div className="relative py-4 md:py-12">
            {/* Scrolling carousel - duplicated for seamless infinite loop */}
            <div className="inline-flex w-full flex-nowrap overflow-x-hidden py-4 [mask-image:_linear-gradient(to_right,transparent_0,_black_128px,_black_calc(100%-128px),transparent_100%)] md:py-8">
              <div className="animate-testimonial-scroll flex gap-6">
                {[...testimonials, ...testimonials, ...testimonials, ...testimonials].map(
                  (testimonial, index) => (
                    <div
                      // oxlint-disable-next-line react/no-array-index-key -- static marketing carousel; list intentionally duplicated 4x, never reordered/filtered
                      key={`${testimonial.id}-${index}`}
                      className="w-[280px] shrink-0 md:w-[550px]"
                    >
                      <Card className="h-full border-0 bg-white shadow-lg transition-all duration-300 hover:-translate-y-2 hover:scale-105 hover:shadow-xl">
                        <CardContent className="flex h-full flex-col p-5 md:p-10">
                          <div className="mb-4 flex justify-center md:mb-6">
                            <Badge className="border-green-200 bg-green-100 px-3 py-1 text-sm text-green-800 hover:bg-green-100 md:px-4 md:py-2 md:text-base">
                              {testimonial.increase}
                            </Badge>
                          </div>

                          <div className="flex flex-1 flex-col justify-between text-center">
                            <p className="mb-4 text-sm italic leading-relaxed text-gray-700 md:mb-6 md:text-xl">
                              "{testimonial.text}"
                            </p>
                            <div className="text-sm font-semibold text-black md:text-lg">
                              {testimonial.company}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Partners Carousel - Hidden until we have real partners */}
        {/* <section className="overflow-hidden px-4 py-20">
        <div className="container mx-auto max-w-6xl">
          <h2 className="mb-12 text-center text-2xl font-bold text-black md:text-3xl">
            Нам доверяют лучшие компании
          </h2>

          <div className="relative">
            <div className="animate-scroll flex space-x-12">
              {[...partnerLogos, ...partnerLogos].map((partner, index) => (
                <div
                  key={`${partner.id}-${index}`}
                  className="flex-shrink-0 rounded-xl bg-white p-8 shadow-md transition-all hover:shadow-lg"
                >
                  <div className="mb-2 text-4xl">{partner.logo}</div>
                  <div className="text-sm text-gray-500">{partner.name}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section> */}

        {/* Modern Contact Form Section */}
        <section id="contact-form" className="bg-gradient-to-br from-gray-900 to-black px-4 py-20">
          <div className="container mx-auto max-w-4xl">
            <div className="overflow-hidden rounded-3xl bg-white shadow-2xl">
              <div className="grid md:grid-cols-2">
                {/* Left side - Info */}
                <div className="bg-gray-50 p-8 md:p-12">
                  <h2 className="mb-4 text-3xl font-bold text-black md:text-4xl">
                    Готовы увеличить продажи?
                  </h2>
                  <p className="mb-8 text-gray-600">
                    Оставьте заявку и получите персональную консультацию от нашего менеджера
                  </p>

                  <div className="space-y-6">
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="mt-0.5 size-6 shrink-0 text-green-500" />
                      <div>
                        <div className="font-semibold text-black">Быстрый запуск</div>
                        <div className="text-sm text-gray-600">Интеграция за 1 день</div>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <CheckCircle className="mt-0.5 size-6 shrink-0 text-green-500" />
                      <div>
                        <div className="font-semibold text-black">Бесплатно для магазина</div>
                        <div className="text-sm text-gray-600">Без скрытых платежей</div>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <CheckCircle className="mt-0.5 size-6 shrink-0 text-green-500" />
                      <div>
                        <div className="font-semibold text-black">Персональный менеджер</div>
                        <div className="text-sm text-gray-600">Поддержка 24/7</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right side - Form */}
                <div className="p-8 md:p-12">
                  <form
                    onSubmit={e => {
                      e.preventDefault();
                      form.handleSubmit().catch((err: unknown) => {
                        console.error('Form submission failed:', err);
                      });
                    }}
                    className="space-y-6"
                  >
                    <form.Field name="name">
                      {field => (
                        <div>
                          <label
                            htmlFor={field.name}
                            className="mb-2 block text-sm font-medium text-gray-700"
                          >
                            Ваше имя
                          </label>
                          <Input
                            type="text"
                            id={field.name}
                            name={field.name}
                            placeholder="Иван Иванов"
                            value={field.state.value}
                            onChange={e => {
                              field.handleChange(e.target.value);
                            }}
                            onBlur={field.handleBlur}
                            className="w-full rounded-xl border-gray-200 px-4 py-3 text-base transition-all focus:border-black focus:ring-2 focus:ring-black"
                            required
                          />
                          {field.state.meta.errors.length > 0 ? (
                            <p className="mt-1 text-xs text-red-600">
                              {String(
                                field.state.meta.errors[0]?.message ?? field.state.meta.errors[0]
                              )}
                            </p>
                          ) : null}
                        </div>
                      )}
                    </form.Field>

                    <form.Field name="phone">
                      {field => (
                        <div>
                          <label
                            htmlFor={field.name}
                            className="mb-2 block text-sm font-medium text-gray-700"
                          >
                            Телефон
                          </label>
                          <Input
                            type="tel"
                            id={field.name}
                            name={field.name}
                            placeholder="+7 (999) 999-99-99"
                            value={field.state.value}
                            onChange={e => {
                              field.handleChange(e.target.value);
                            }}
                            onBlur={field.handleBlur}
                            className="w-full rounded-xl border-gray-200 px-4 py-3 text-base transition-all focus:border-black focus:ring-2 focus:ring-black"
                            required
                          />
                          {field.state.meta.errors.length > 0 ? (
                            <p className="mt-1 text-xs text-red-600">
                              {String(
                                field.state.meta.errors[0]?.message ?? field.state.meta.errors[0]
                              )}
                            </p>
                          ) : null}
                        </div>
                      )}
                    </form.Field>

                    <form.Subscribe selector={s => s.isSubmitting}>
                      {isSubmitting => (
                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full rounded-xl bg-black py-4 text-lg text-white shadow-lg transition-all hover:scale-[1.02] hover:bg-gray-900 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isSubmitting ? 'Отправка...' : 'Отправить заявку'}
                          <ArrowRight className="ml-2 size-5" />
                        </Button>
                      )}
                    </form.Subscribe>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200" />
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="bg-white px-2 text-gray-500">или</span>
                      </div>
                    </div>

                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleTelegramClick}
                      className="w-full rounded-xl border-2 py-4 text-lg transition-all hover:scale-[1.02] hover:bg-black hover:text-white"
                    >
                      Написать в Телеграм
                      <ArrowUpRight className="ml-2 size-5" />
                    </Button>

                    {submitMessage ? (
                      <div
                        className={cn(
                          'rounded-lg p-3 text-center text-sm',
                          submitMessage.type === 'success'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        )}
                      >
                        {submitMessage.text}
                      </div>
                    ) : null}

                    <p className="text-center text-xs text-gray-500">
                      Нажимая кнопку, вы соглашаетесь с{' '}
                      <a
                        href="/confidentiality.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-gray-700"
                      >
                        условиями обработки персональных данных
                      </a>
                    </p>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-black py-12 text-white">
          <div className="container mx-auto px-4">
            <div className="flex flex-col items-center justify-between md:flex-row">
              <div className="mb-4 flex items-center space-x-3 md:mb-0">
                <img
                  src="/small-logo.svg"
                  alt="ЛОНА ФИНАНС"
                  className="size-8 brightness-0 invert"
                  onError={e => {
                    e.currentTarget.style.display = 'none';
                    const next = e.currentTarget.nextElementSibling;
                    if (next) {
                      next.classList.remove('hidden');
                      next.classList.add('flex');
                    }
                  }}
                />
                <div className="hidden size-8 items-center justify-center rounded-lg bg-white">
                  <span className="text-lg font-bold text-black">L</span>
                </div>
                <div>
                  <span className="text-xl font-bold">ЛОНА</span>
                  <span className="ml-1 text-xl font-light text-gray-400">ФИНАНС</span>
                </div>
              </div>
              <p className="text-gray-400">© ЛОНА {new Date().getFullYear()} Все права защищены</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingPage;
